import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { BackLink } from '@siliconx/shared/ui/BackLink';
import { SubmissionStatusBadge } from '@siliconx/shared/ui/SubmissionStatusBadge';

// RUF-550: submission detail is portfolio self-service — no completeness gate.
export const Route = createFileRoute('/submissions/$submissionId')({
  component: SubmissionDetailPage,
});

async function fetchSubmission(id: string) {
  const res = await api.api.client.submissions[':id'].$get({ param: { id } });
  if (res.status === 404) {
    const err = new Error('Submission not found') as Error & { status: number };
    err.status = 404;
    throw err;
  }
  await throwIfNotOk(res);
  return res.json();
}

function Row({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-sm text-foreground">{value ?? '—'}</dd>
    </div>
  );
}

function SubmissionDetailPage() {
  const { submissionId } = Route.useParams();

  const submissionQ = useQuery({
    queryKey: qk.submissions.detail(submissionId),
    queryFn: () => fetchSubmission(submissionId),
  });

  // 404 gets its own friendly "not found" panel (Consequence-First) rather
  // than the generic "couldn't load" retry copy QueryBoundary renders for
  // transient errors.
  const apiError = submissionQ.error as (Error & { status?: number }) | null;
  if (submissionQ.isError && apiError?.status === 404) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-lg font-semibold text-foreground">Submission not found</p>
        <p className="text-sm text-muted-foreground">
          This submission does not exist or belongs to another organization.
        </p>
        <Link to="/submissions" className="text-sm font-medium text-brand-600 hover:underline">
          Back to submissions
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-center gap-4">
        <BackLink as={Link} to="/submissions">
          Submissions
        </BackLink>
        <PageHeader title="Submission Detail" />
      </div>

      <QueryBoundary query={submissionQ} errorTitle="Couldn't load submission.">
        {(data) => (
          <div className="overflow-hidden rounded-lg border border-border bg-card px-6">
            <dl className="divide-y divide-border">
              <Row label="Asset Class" value={data.assetClass} />
              <Row label="Make" value={data.make} />
              <Row label="Model" value={data.model} />
              <Row label="Serial Number" value={data.serialNumber} />
              <Row label="Condition" value={data.condition} />
              <Row label="Quantity" value={data.quantity} />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3">
                <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                <dd className="col-span-2">
                  <SubmissionStatusBadge value={data.status} />
                </dd>
              </div>
              <Row
                label="Submitted"
                value={data.createdAt ? new Date(data.createdAt).toLocaleString() : '—'}
              />
              {data.notes && <Row label="Notes" value={data.notes} />}
            </dl>
          </div>
        )}
      </QueryBoundary>
    </div>
  );
}
